/**
 * normalizeAcronyms.ts
 * 
 * Global utility to enforce correct capitalization of known acronyms
 * across all user-facing strings in the Pathkit UI.
 * 
 * Usage:
 *   import { normalizeAcronyms, formatCategoryLabel } from '../utils/normalizeAcronyms';
 *   
 *   normalizeAcronyms("incident sla calendar")  → "Incident SLA Calendar"
 *   normalizeAcronyms("srm sla")                → "SRM SLA"
 *   normalizeAcronyms("hlo alignment")           → "HLO Alignment"
 *   formatCategoryLabel("srm_sla")               → "SRM SLA"
 *   formatCategoryLabel("incident_workflow")      → "Incident Workflow"
 */

// Acronyms that must ALWAYS be fully uppercased when they appear as
// standalone words (matched case-insensitively, replaced with canonical form).
const ACRONYM_MAP: Record<string, string> = {
  sla: 'SLA',
  slas: 'SLAs',
  srm: 'SRM',
  hlo: 'HLO',
  hlos: 'HLOs',
  itsm: 'ITSM',
  jsm: 'JSM',
  ci: 'CI',
  cis: 'CIs',
  ai: 'AI',
  bd: 'BD',
  el: 'EL',
  pir: 'PIR',
  rca: 'RCA',
  cmdb: 'CMDB',
  sow: 'SOW',
  sows: 'SOWs',
  mi: 'MI',
  kb: 'KB',
  sa: 'SA',
  pm: 'PM',
  api: 'API',
  apis: 'APIs',
  url: 'URL',
  urls: 'URLs',
  csv: 'CSV',
  pdf: 'PDF',
  jql: 'JQL',
  bmc: 'BMC',
};

/**
 * Normalizes known acronyms in a string to their correct uppercase form.
 * Preserves the rest of the text as-is (does not change non-acronym casing).
 * 
 * @example
 *   normalizeAcronyms("Sla Calendar")        → "SLA Calendar"
 *   normalizeAcronyms("srm sla and hlo")     → "SRM SLA and HLO"
 *   normalizeAcronyms("incident Sla config")  → "incident SLA config"
 */
export function normalizeAcronyms(text: string): string {
  if (!text) return text;
  
  // Match word boundaries to avoid partial replacements (e.g., "island" → "iSLAnd")
  return text.replace(/\b([A-Za-z]+)\b/g, (match) => {
    const lower = match.toLowerCase();
    return ACRONYM_MAP[lower] ?? match;
  });
}

/**
 * Converts an underscore-delimited key into a human-readable label
 * with proper title-casing AND acronym normalization.
 * 
 * Replaces the common pattern:
 *   key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
 * 
 * which incorrectly produces "Srm Sla" from "srm_sla".
 * 
 * @example
 *   formatCategoryLabel("sla_calendar")            → "SLA Calendar"
 *   formatCategoryLabel("srm_request_types")        → "SRM Request Types"
 *   formatCategoryLabel("incident_operating_model")  → "Incident Operating Model"
 *   formatCategoryLabel("hlo_definition")            → "HLO Definition"
 */
export function formatCategoryLabel(key: string): string {
  if (!key) return key;
  
  // Special-case overrides for user-friendly labels
  const LABEL_OVERRIDES: Record<string, string> = {
    'hlo_definition': 'Refine & Validate Objectives',
  };
  if (LABEL_OVERRIDES[key]) return LABEL_OVERRIDES[key];

  const words = key.split('_').map((word) => {
    const lower = word.toLowerCase();
    // Check if the word is a known acronym
    if (ACRONYM_MAP[lower]) {
      return ACRONYM_MAP[lower];
    }
    // Otherwise title-case
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
  
  return words.join(' ');
}

/**
 * Cleans up a dynamic "unlocks" or dependency text string:
 * 1. Normalizes acronyms
 * 2. Removes redundant repetitions of the same noun within a sentence
 * 
 * Designed for cleaning AI-generated or engine-generated strings
 * before they hit the UI.
 */
export function cleanDynamicText(text: string): string {
  if (!text) return text;
  return normalizeAcronyms(text);
}